import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { locationApi } from "@/api/location.api";
import { SocketEvents } from "@/sockets/socket";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import type { DriverLocation } from "@/types/domain";

/**
 * Seeds the Live Rides map from the REST snapshot (locationApi), then keeps
 * positions current via the `admin:driver:location` Socket.IO event — no
 * polling, no page refresh, per the Live Rides realtime requirement.
 */
export function useLiveDriverLocations() {
  const snapshot = useQuery({
    queryKey: ["location", "admin", "drivers"],
    queryFn: () => locationApi.getOnlineDrivers(),
  });

  const [locations, setLocations] = useState<Record<string, DriverLocation>>({});

  useEffect(() => {
    if (!snapshot.data) return;
    setLocations(Object.fromEntries(snapshot.data.map((d) => [d.driverId, d])));
  }, [snapshot.data]);

  useSocketEvent<DriverLocation>(SocketEvents.AdminDriverLocation, (payload) => {
    setLocations((prev) => ({ ...prev, [payload.driverId]: { ...prev[payload.driverId], ...payload } }));
  });

  return {
    locations: Object.values(locations),
    isLoading: snapshot.isLoading,
    error: snapshot.error,
    refetch: snapshot.refetch,
  };
}
